import CustomHeader from 'components/ReusableComponents/HeaderTitles'
import InfoIcon from 'components/ReusableComponents/Icons/Info'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import Dropdown from 'components/ReusableComponents/ManageHotel/Dropdown'
import MultiSelectDropdown from 'components/ReusableComponents/MultiSelectDropdown'
import Table, { TitleCell, TitleRow } from 'components/ReusableComponents/Table'
import Tab from 'components/ReusableComponents/Tabs'
import useSyncedState from 'hooks/useSyncedState'
import { parentChainNames, PMSnames } from 'main/hotelBrandsAndChains'
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import ReactTooltip from 'react-tooltip'

import { HotelCard } from './HotelCard/HotelCard'
import styles from './Hotels.module.scss'
import {
  NEEDS_ATTENTION_CHIP_LABELS,
  NEEDS_ATTENTION_CHIP_ORDER,
  countNeedsAttentionProblems,
  countPmsForNeedsAttentionProblem,
  isPmsNeedsAttentionProblem
} from './needsAttentionProblems'
import {
  filterByChainAndParentChain,
  filterByGroupAndChain,
  filterByGroupAndParentChain
} from './sortHelpers'
import { useHotelsData } from './useData'

const sortableIndexes = [
  'hotel',
  'group',
  'brand',
  'csm',
  'addedOn',
  'updatedOn',
  'status'
]

const sortableIndexesWithServiceStartDate = [
  'hotel',
  'group',
  'brand',
  'csm',
  'addedOn',
  'serviceStartDate',
  'status'
]

const hotelStatusByType = {
  onboarding: {
    PendingCampaignBrandRegistration: 'Pending campaign reg.',
    PendingCampaignBrandRegistrationDeclined: 'Pending campaign reg. declined',
    PendingPhoneNumberSetup: 'Pending telecom setup',
    PendingCampaignProviderImport: 'Pending campaign import',
    HotelConfigurationStage: 'Hotel configuration stage'
  },
  upAndRunning: {
    BasicAccount: 'Basic account',
    MediumAccount: 'Medium account',
    LargeAccount: 'Large account',
    AboutToLose: 'About to lose'
  },
  needsAttention: {
    PendingCampaignBrandRegistration: 'Pending campaign reg.',
    PendingPhoneNumberSetup: 'Pending telecom setup',
    PendingCampaignProviderImport: 'Pending campaign import',
    HotelConfigurationStage: 'Hotel Configuration Stage',
    BasicAccount: 'Basic account',
    MediumAccount: 'Medium account',
    LargeAccount: 'Large account',
    AboutToLose: 'About to lose'
  },
  churned: {
    Churned: 'Churned'
  },
  paused: {
    Paused: 'Paused'
  },
  followUp: {},
  disconnected: {},
  deleted: {},
  // API-supported list type (e.g. /hotels?type=allActive); no status subset in this view
  allActive: {}
}

const Hotels = () => {
  const [type] = useSyncedState('type', 'hotelsType', 'onboarding')
  const isHealtyHotel = type === 'upAndRunning'
  const isOnboarding = type === 'onboarding'
  const sortIndexes = isOnboarding ? sortableIndexesWithServiceStartDate : sortableIndexes
  const usesStatusIssueColumn = !isOnboarding && !isHealtyHotel
  const showNeedsAttentionSinceColumn = type === 'needsAttention'
  const needsAttentionSinceExtraColumns = showNeedsAttentionSinceColumn ? 2 : 0

  const {
    sortedAndFilteredHotels,
    baseFilteredHotels,
    hotels,
    csmUsers,
    isLoading,
    sort: { sortParam, setSortParam, sortDirection, setSortDirection },
    filter: {
      selectedParentChain,
      setSelectedParentChain,
      selectedChain,
      setSelectedChain,
      selectedGroup,
      setSelectedGroup,
      selectedStatus,
      setSelectedStatus,
      selectedCsmIds,
      setSelectedCsmIds,
      selectedProblemGroup,
      setSelectedProblemGroup,
      selectedProblemPms,
      setSelectedProblemPms,
      clearFilters
    },
    search: { searchQuery, setSearchQuery },
    refetchData
  } = useHotelsData(type)

  const needsAttentionProblemCounts = useMemo(
    () =>
      type === 'needsAttention' ? countNeedsAttentionProblems(baseFilteredHotels) : {},
    [type, baseFilteredHotels]
  )

  const needsAttentionPmsCounts = useMemo(() => {
    if (type !== 'needsAttention' || !isPmsNeedsAttentionProblem(selectedProblemGroup)) {
      return []
    }
    return countPmsForNeedsAttentionProblem(
      baseFilteredHotels,
      selectedProblemGroup,
      pms => PMSnames[pms] || pms
    )
  }, [type, baseFilteredHotels, selectedProblemGroup])

  function selectProblemGroup(key) {
    const next = selectedProblemGroup === key ? 'all' : key
    setSelectedProblemGroup(next)
    setSelectedProblemPms('all')
  }

  function handleTitleClick(i) {
    const updatedOnIdx = sortIndexes.indexOf('updatedOn')
    const statusColumnIndex =
      sortIndexes.indexOf('status') + needsAttentionSinceExtraColumns
    const sinceDateColumnIndex = showNeedsAttentionSinceColumn ? updatedOnIdx + 1 : -1
    const sinceDaysColumnIndex = showNeedsAttentionSinceColumn ? updatedOnIdx + 2 : -1

    if (i === sinceDaysColumnIndex) {
      if (sortParam === 'sinceDays') {
        setSortDirection(d => (d === 'up' ? 'down' : 'up'))
      } else {
        setSortParam('sinceDays')
        setSortDirection('down')
      }
      return
    }

    if (i === sinceDateColumnIndex) {
      if (sortParam === 'sinceDate') {
        setSortDirection(d => (d === 'up' ? 'down' : 'up'))
      } else {
        setSortParam('sinceDate')
        setSortDirection('down')
      }
      return
    }

    if (usesStatusIssueColumn && i === statusColumnIndex) {
      if (sortParam !== 'status' && sortParam !== 'issue') {
        setSortParam('status')
        setSortDirection('down')
      } else if (sortParam === 'status' && sortDirection === 'down') {
        setSortParam('issue')
        setSortDirection('down')
      } else if (sortParam === 'issue' && sortDirection === 'down') {
        setSortParam('status')
        setSortDirection('up')
      } else if (sortParam === 'status' && sortDirection === 'up') {
        setSortParam('issue')
        setSortDirection('up')
      } else if (sortParam === 'issue' && sortDirection === 'up') {
        setSortParam('status')
        setSortDirection('down')
      } else {
        setSortParam('status')
        setSortDirection('down')
      }
      return
    }

    const logicalSortIndex =
      showNeedsAttentionSinceColumn && updatedOnIdx >= 0 && i > updatedOnIdx
        ? i - needsAttentionSinceExtraColumns
        : i

    if (sortParam === sortIndexes[logicalSortIndex]) {
      setSortDirection(d => (d === 'up' ? 'down' : 'up'))
    }

    if (sortIndexes[logicalSortIndex]) {
      setSortParam(sortIndexes[logicalSortIndex])
    }
  }

  const sortIndex = (() => {
    const updatedOnIdx = sortIndexes.indexOf('updatedOn')
    if (showNeedsAttentionSinceColumn && sortParam === 'sinceDate') {
      return updatedOnIdx + 1
    }
    if (showNeedsAttentionSinceColumn && sortParam === 'sinceDays') {
      return updatedOnIdx + 2
    }
    const base = sortIndexes.indexOf(sortParam)
    if (usesStatusIssueColumn && (sortParam === 'status' || sortParam === 'issue')) {
      return sortIndexes.indexOf('status') + needsAttentionSinceExtraColumns
    }
    if (
      showNeedsAttentionSinceColumn &&
      updatedOnIdx >= 0 &&
      base > updatedOnIdx
    ) {
      return base + needsAttentionSinceExtraColumns
    }
    return base
  })()

  const hotelStatusOptions = hotelStatusByType[type] ?? {}

  const filteredForGroupName = filterByChainAndParentChain(
    hotels,
    selectedChain,
    selectedParentChain,
    selectedStatus
  )

  const filteredForChain = filterByGroupAndParentChain(
    hotels,
    selectedParentChain,
    selectedGroup,
    selectedStatus
  )

  const filteredForParentChain = filterByGroupAndChain(
    hotels,
    selectedChain,
    selectedGroup,
    selectedStatus
  )

  return (
    <div className={styles.wrapper}>
      <ReactTooltip id="hotels-tooltip" place="top" type="dark" effect="solid" />

      <CustomHeader title={`Hotels (${hotels.length})`} isFixed width={1100}>
        <div className={styles.helpText}>
          <InfoIcon /> Add new hotels from <Link to="/contracts"> contracts screen.</Link>
        </div>
      </CustomHeader>

      {isLoading ? (
        <LoadingIndicator />
      ) : (
        <>
          <ReactTooltip />
          <Table
            widths={
              showNeedsAttentionSinceColumn
                ? // 10 cols, same 1080px total as the default 8-col layout
                  [210, 125, 102, 40, 80, 80, 88, 70, 128, 157]
                : [245, 160, 120, 40, 90, 90, 170, 165]
            }
            sortIndex={sortIndex}
            sortDirection={sortDirection}
            handleTitleCellClick={handleTitleClick}
          >
            <div className={styles.headerWrapper}>
              <Tab.Parent>
                <Tab exact to="/hotels?type=onboarding">
                  Onboarding {type === 'onboarding' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=upAndRunning">
                  Up and Running{' '}
                  {type === 'upAndRunning' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=needsAttention">
                  Needs Attention{' '}
                  {type === 'needsAttention' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=churned">
                  Churned
                  {type === 'churned' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=paused">
                  Paused
                  {type === 'paused' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=disconnected">
                  Disconnected{' '}
                  {type === 'disconnected' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=followUp">
                  Remind later {type === 'followUp' && !isLoading && `(${hotels.length})`}
                </Tab>
                <Tab exact to="/hotels?type=deleted">
                  Deleted {type === 'deleted' && !isLoading && `(${hotels.length})`}
                </Tab>
              </Tab.Parent>
              {type === 'needsAttention' && (
                <div className={styles.problemChipStack}>
                  <div className={styles.problemChips} role="group" aria-label="Problem groups">
                    <button
                      type="button"
                      className={`${styles.problemChip}${
                        selectedProblemGroup === 'all' ? ` ${styles.problemChipActive}` : ''
                      }`}
                      onClick={() => selectProblemGroup('all')}
                    >
                      All ({baseFilteredHotels.length})
                    </button>
                    {NEEDS_ATTENTION_CHIP_ORDER.filter(
                      key => (needsAttentionProblemCounts[key] || 0) > 0
                    ).map(key => (
                      <button
                        key={key}
                        type="button"
                        className={`${styles.problemChip}${
                          selectedProblemGroup === key ? ` ${styles.problemChipActive}` : ''
                        }`}
                        onClick={() => selectProblemGroup(key)}
                      >
                        {NEEDS_ATTENTION_CHIP_LABELS[key]} ({needsAttentionProblemCounts[key]})
                      </button>
                    ))}
                  </div>
                  {needsAttentionPmsCounts.length > 0 && (
                    <div
                      className={styles.problemPmsChips}
                      role="group"
                      aria-label="PMS breakdown"
                    >
                      {needsAttentionPmsCounts.map(({ pms, label, count }) => (
                        <button
                          key={pms}
                          type="button"
                          className={`${styles.problemPmsChip}${
                            selectedProblemPms === pms ? ` ${styles.problemChipActive}` : ''
                          }`}
                          onClick={() =>
                            setSelectedProblemPms(selectedProblemPms === pms ? 'all' : pms)
                          }
                        >
                          {label} ({count})
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className={styles.header}>
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All groups',
                    ...(selectedGroup === 'all'
                      ? {}
                      : { [selectedGroup]: selectedGroup }),
                    ...filteredForGroupName.reduce((prev, curr) => {
                      return {
                        ...prev,
                        [curr.merchantName]:
                          curr.merchantName === 'Independently Managed Hotels'
                            ? '- Independently Managed'
                            : curr.merchantName
                      }
                    }, {})
                  }}
                  value={selectedGroup}
                  onChange={e => setSelectedGroup(e.target.value)}
                />
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All brands',
                    ...(selectedParentChain === 'all'
                      ? {}
                      : {
                          [selectedParentChain]: parentChainNames[selectedParentChain]
                        }),
                    ...filteredForParentChain.reduce(
                      (prev, curr) => ({
                        ...prev,
                        [curr.parentChainCode]: parentChainNames[curr.parentChainCode]
                      }),
                      {}
                    )
                  }}
                  value={selectedParentChain}
                  onChange={e => setSelectedParentChain(e.target.value)}
                />
                <Dropdown
                  className={styles.dropdown}
                  options={{
                    all: ' All chains',
                    ...(selectedChain === 'all'
                      ? {}
                      : {
                          [selectedChain]:
                            selectedChain === 'i' ? 'independent' : selectedChain
                        }),
                    ...filteredForChain.reduce(
                      (prev, curr) => ({
                        ...prev,
                        [curr.chainCode]:
                          curr.chainCode === 'i' ? 'independent' : curr.chainCode
                      }),
                      {}
                    )
                  }}
                  value={selectedChain}
                  onChange={e => setSelectedChain(e.target.value)}
                />
                {Object.entries(hotelStatusOptions).length > 0 && (
                  <MultiSelectDropdown
                    options={hotelStatusOptions}
                    value={selectedStatus}
                    itemName="statuses"
                    onChangeValue={setSelectedStatus}
                    className={styles.statusFilter}
                    hasUnselectButton
                  />
                )}
                <MultiSelectDropdown
                  options={Object.fromEntries(
                    csmUsers.map(csm => [csm.id, csm.fullName])
                  )}
                  value={selectedCsmIds}
                  className={styles.statusFilter}
                  onChangeValue={value => setSelectedCsmIds(value)}
                  hasUnselectButton
                  itemName="CSM"
                  itemNamePlural="CSMs"
                />
                <input
                  autoFocus={true}
                  type="search"
                  className={styles.searchFilter}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Type anything to filter"
                />
                <button onClick={() => clearFilters()} className={styles.clearFilters}>
                  Clear filters{' '}
                </button>
              </div>
              <TitleRow>
                <TitleCell>HOTEL</TitleCell>
                <TitleCell>GROUP</TitleCell>
                <TitleCell>BRAND</TitleCell>
                <TitleCell>CSM</TitleCell>
                <TitleCell>ADDED ON</TitleCell>
                <TitleCell>{isOnboarding ? 'START DATE' : 'UPDATED ON'}</TitleCell>
                {showNeedsAttentionSinceColumn ? (
                  <TitleCell>SINCE (DATE)</TitleCell>
                ) : null}
                {showNeedsAttentionSinceColumn ? (
                  <TitleCell>SINCE (DAYS)</TitleCell>
                ) : null}
                <TitleCell>
                  {isHealtyHotel ? 'TAGS' : isOnboarding ? 'STATUS' : 'STATUS / ISSUE'}
                </TitleCell>
                <TitleCell></TitleCell>
              </TitleRow>
            </div>

            {sortedAndFilteredHotels.map((hotel, index) => (
              <HotelCard
                index={sortedAndFilteredHotels.length - index}
                key={hotel.id}
                hotel={hotel}
                isHealthyHotel={isHealtyHotel}
                isOnboarding={isOnboarding}
                category={type}
                showNeedsAttentionSinceColumn={showNeedsAttentionSinceColumn}
                refetchData={refetchData}
              />
            ))}
          </Table>
        </>
      )}
    </div>
  )
}

export default Hotels
